/**
 * Timeline event types and interfaces
 * Used for sphere timeline feature with Cosmos DB storage
 */

/**
 * Timeline Event from Cosmos DB sphere-timeline database
 * Events are cryptographically signed for audit integrity and chronologically linked
 */
export interface TimelineEvent {
  id: string;                    // UUID
  sphereId: string;              // Associated sphere ID
  timestamp: string;             // ISO 8601 timestamp
  eventType: TimelineEventType;  // Event type
  actor: string;                 // User ID who triggered the event
  prevEventId: string | null;    // Previous event ID (chronological chaining)
  hash: string;                  // SHA-256 hash of event content
  signature: string;             // RSA-256 signature (Base64 encoded)
  keyVersion: string;            // Key Vault key ID for signature verification
  payload: any;                  // Event-specific data
}

/**
 * Timeline event types.
 *
 * These ARE the Observation Ledger's `LedgerEntryType` values (EAINT-764 Stage 2). The timeline is
 * served from `ledger_entries`, and `toPublicEvent` passes `entry_type` through as `eventType`
 * verbatim — so the legacy PascalCase vocabulary this once declared no longer matches anything the
 * API sends. Every value below corresponds 1:1 to a member of
 * `graph-service/src/ledger/envelope.ts`'s union; keep the two in step.
 *
 * The old vocabulary was both wrong and wider: it declared 31 types where the ledger has 20, and
 * several (ArcActivated / ArcCompleted / ArcCancelled / ArcFailed, RoleMemberAdded/Removed,
 * FactorModeChanged, AttachmentUpdated) never had a writer at all — they were UI-side wishful
 * thinking that made the switch look more complete than it was.
 */
export type TimelineEventType =
  | 'aspect_observation'
  | 'aspect_validation'
  | 'aspect_attached'
  | 'aspect_detached'
  | 'arc_transition'
  | 'arc_lifecycle'
  | 'arc_answered'
  | 'check_in'
  | 'factor_lifecycle'
  | 'perspective_assigned'
  | 'perspective_unassigned'
  | 'sphere_created'
  | 'sphere_property_changed'
  | 'attachment_added'
  | 'attachment_removed'
  | 'permission_changed'
  | 'stage_action'
  | 'status_propagated'
  | 'velocity_changed'
  | 'impact_changed'
  | 'task_completed';

/**
 * Alias for `TimelineEventType` under the ledger's own name. `types/stream.d.ts` (Stream tool,
 * specs/stream-trajectory-tools.md) imports this name specifically — `skipLibCheck` was masking
 * the fact that no such export existed until this alias was added. Kept as a separate exported
 * name (not a rename of `TimelineEventType`) so the plain `/timeline` route's existing consumers
 * are untouched.
 */
export type LedgerEntryType = TimelineEventType;

/**
 * Event categories for filtering and UI organization
 */
export type TimelineEventCategory =
  | 'system'
  | 'workflow'
  | 'security'
  | 'userAction'
  | 'admin';

/**
 * Event type -> category.
 *
 * Keys are `LedgerEntryType` values and the categories MIRROR graph-service's
 * `LEDGER_ENTRY_CATEGORY_MAP` (app/helpers/timeline/ledgerEntryCategory.ts), which is what the
 * `?category=` filter actually resolves server-side. When these two disagree the UI offers a filter
 * that returns something different from what it says — so treat that file as authoritative and keep
 * this in step with it.
 */
export const EVENT_CATEGORY_MAP: Record<TimelineEventType, TimelineEventCategory> = {
  sphere_created: 'system',
  sphere_property_changed: 'userAction',
  permission_changed: 'security',
  arc_transition: 'workflow',
  arc_lifecycle: 'workflow',
  arc_answered: 'userAction',
  stage_action: 'workflow',
  check_in: 'userAction',
  task_completed: 'workflow',
  factor_lifecycle: 'system',
  perspective_assigned: 'userAction',
  perspective_unassigned: 'userAction',
  aspect_observation: 'userAction',
  aspect_validation: 'system',
  aspect_attached: 'admin',
  aspect_detached: 'admin',
  attachment_added: 'userAction',
  attachment_removed: 'userAction',
  status_propagated: 'workflow',
  velocity_changed: 'system',
  impact_changed: 'system',
};

/**
 * Query parameters for timeline API requests
 */
export interface TimelineQueryParams {
  sphereId: string;              // Required: Sphere to query timeline for
  eventTypes?: TimelineEventType[]; // Optional: Filter by specific event types
  category?: TimelineEventCategory; // Optional: Filter by category
  startDate?: string;            // Optional: ISO timestamp - events after this date
  endDate?: string;              // Optional: ISO timestamp - events before this date
  limit?: number;                // Optional: Max events to return (default: 50, max: 100)
  offset?: number;               // Optional: Number of events to skip (for pagination)
  sortOrder?: 'asc' | 'desc';    // Optional: Sort order (default: 'desc' - most recent first)
}

/**
 * API response from timeline query
 */
export interface TimelineQueryResponse {
  success: boolean;
  data: TimelineEvent[];
  count: number;                 // Number of events returned
  total: number;                 // Total events matching filters
  hasMore: boolean;              // Whether more events are available
  query: {
    sphereId: string;
    filters: {
      eventTypes?: TimelineEventType[];
      category?: TimelineEventCategory;
      dateRange?: {
        start?: string;
        end?: string;
      };
    };
    pagination: {
      limit: number;
      offset: number;
      sortOrder: 'asc' | 'desc';
    };
  };
}

/**
 * Timeline filter state for UI components
 */
export interface TimelineFilters {
  eventTypes: TimelineEventType[];
  categories: TimelineEventCategory[];
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
}

/**
 * Event type metadata for UI display
 */
export interface TimelineEventMetadata {
  eventType: TimelineEventType;
  category: TimelineEventCategory;
  displayName: string;          // Localized display name
  description: string;          // Localized description
  icon: string;                 // PrimeIcon class name
  color: string;                // CSS color for category
}

/**
 * Feed filter types for Home Mode activity stream filtering
 */

/** Category-level filter with child event types */
export interface FeedFilterCategory {
  id: string;                          // e.g., 'userAction', 'workflow'
  labelKey: string;                    // i18n key
  icon: string;                        // PrimeIcon class
  enabled: boolean;                    // Category-level toggle
  eventTypes: FeedFilterEventType[];   // Child filters (empty for 'conversations')
}

/** Individual event type filter within a category */
export interface FeedFilterEventType {
  value: TimelineEventType;
  labelKey: string;
  icon: string;
  enabled: boolean;
  subtypes?: FeedFilterSubtype[];      // e.g., perspective direction
}

/** Third-level subtype filter (e.g., inbound/outbound) */
export interface FeedFilterSubtype {
  id: string;                          // e.g., 'inbound', 'outbound'
  labelKey: string;
  enabled: boolean;
}

/** Serialized filter state for localStorage persistence */
export interface FeedFilterState {
  version: 1;                          // Schema version for migration
  conversationsEnabled: boolean;
  disabledCategories: string[];        // Category IDs that are OFF
  disabledEventTypes: string[];        // Event type values that are OFF
  disabledSubtypes: string[];          // 'PerspectiveAssigned:outbound' style keys
}

/** All timeline event types as an array for filter computation */
export const ALL_EVENT_TYPES: TimelineEventType[] = Object.keys(
  EVENT_CATEGORY_MAP
) as TimelineEventType[];

/** Default filter state — all filters enabled (show everything) */
export const DEFAULT_FILTER_STATE: FeedFilterState = {
  version: 1,
  conversationsEnabled: true,
  disabledCategories: [],
  disabledEventTypes: [],
  disabledSubtypes: [],
};
